"use client";

import { useEffect, useState } from "react";
import { useSession } from "next-auth/react";
import { useAuth } from "@/context/AuthContext";
import { usePermission } from "@/hooks/usePermission/usePermission";

/**
 * Hook que garante que todas as dependências necessárias para chamadas de API
 * estejam prontas antes de permitir requisições. Inclui um delay de segurança
 * para garantir sincronização completa em produção.
 *
 * @param delayMs - Delay adicional em milissegundos após tudo estar pronto (padrão: 2000ms)
 * @returns Objeto com flags indicando se está pronto e se está carregando
 */
export const useApiReady = (delayMs: number = 250) => {
  const { data: session, status: sessionStatus } = useSession();
  const { token, baseURL, establishment, isLoading: authLoading } = useAuth();
  const { loading: permissionLoading } = usePermission();

  const [isReady, setIsReady] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);

  useEffect(() => {
    const checkReady = () => {
      const sessionReady = sessionStatus !== "loading" && session !== null;
      const authReady = !authLoading && !!token && !!baseURL && !!establishment;
      const permissionsReady = !permissionLoading;

      return sessionReady && authReady && permissionsReady;
    };

    if (checkReady()) {
      setIsInitializing(false);
      const timeoutId = setTimeout(() => {
        setIsReady(true);
      }, delayMs);

      return () => clearTimeout(timeoutId);
    } else {
      setIsInitializing(true);
      setIsReady(false);
    }
  }, [
    sessionStatus,
    session,
    authLoading,
    token,
    baseURL,
    establishment,
    permissionLoading,
    delayMs,
  ]);

  return {
    isReady,
    isInitializing,
    sessionReady: sessionStatus !== "loading" && session !== null,
    authReady: !authLoading && !!token && !!baseURL && !!establishment,
    permissionsReady: !permissionLoading,
  };
};
